import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import {
  Button,
  Grid,
  Input,
  MenuItem,
  Paper,
  Select,
  SnackBar,
} from 'components/atoms';
import { severityType } from 'components/atoms/Alert/Alert.styles';
import { CREATE_ROLE } from 'const';
import { states } from 'const/const';
import { useGetTable } from 'hooks';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Menu, Role, User } from 'types';
import { flatMenus, getErrorsFromBackend } from 'utils';
import { fetchMenus, fetchUsers } from './CreateTaskTemplate.const';
import CreateRoleTemplateLoading from './CreateTaskTemplate.loading';
import { CreateTaskSchema, CreateTaskType } from './CreateTaskTemplate.schema';

const CreateTaskTemplate = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { data: menusFetch, loading: loadingMenus } = useGetTable<Menu[]>(
    fetchMenus,
    [true],
  );

  const menus = useMemo(() => flatMenus(menusFetch), [menusFetch]);

  const { data: users, loading: loadingUsers } = useGetTable<User[]>(
    fetchUsers,
    [true],
  );

  const loadingData = loadingMenus || loadingUsers;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setError,
    formState: { errors },
  } = useForm<CreateTaskType>({
    resolver: yupResolver(CreateTaskSchema),
    defaultValues: {
      name: '',
      enabled: 'true',
      menus: '',
      users: '',
    },
  });

  const [open, setOpen] = useState<severityType>();

  const onClose = () => {
    setOpen(undefined);
    if (open === 'success') navigate('/pages/management/role');
  };

  const onSubmit = async (loginForm: CreateTaskType) => {
    try {
      setLoading(true);
      const { menus, users, enabled, ...form } = loginForm;
      const copyMenus = menus
        ?.replace(/-/g, ',')
        .split(',')
        .filter((item) => item !== '') as string[];
      const plainMenus = Array.from(new Set(copyMenus));
      const plainUsers = users?.split(',').filter((item) => item !== '');
      const isEnabled = enabled === 'true';

      await axios.post<Role>(CREATE_ROLE, {
        ...form,
        enabled: isEnabled,
        menus: plainMenus,
        users: plainUsers,
      });
      setLoading(false);
      setOpen('success');
    } catch (error) {
      setLoading(false);
      const { isGeneral, errorOptions } =
        getErrorsFromBackend<CreateTaskType>(error);

      if (isGeneral) {
        setError(...errorOptions);
        setOpen('error');
      } else
        errorOptions.forEach((error) => {
          setError(...error);
        });
    }
  };

  const name = 'ROLE_' + watch('name');
  const transformName = useMemo(
    () => name?.replace(/\s/g, '_').toUpperCase(),
    [name],
  );

  return (
    <Paper>
      {!loadingData ? (
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3} justify="flex-end">
            <Grid item xs={12}>
              <Input
                label="Nombre"
                fullWidth
                required
                {...register('name')}
                error={!!errors.name}
                helperText={errors.name && errors.name.message}
              />
            </Grid>
            <Grid item xs={12}>
              <Input disabled label="Código" fullWidth value={transformName} />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="enabled"
                control={control}
                render={({ field }) => {
                  return (
                    <Select
                      required
                      value={field.value}
                      onChange={field.onChange}
                      label="Estado"
                      fullWidth
                    >
                      {states.map(({ value, name }) => (
                        <MenuItem
                          key={name}
                          label={`${name}`}
                          value={`${value}`}
                        >
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="menus"
                control={control}
                render={({ field }) => {
                  return (
                    <Select
                      label="Menus"
                      fullWidth
                      required
                      multiple
                      enableFilter
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.menus}
                      helperText={errors.menus && errors.menus.message}
                    >
                      {menus.map(({ id, title }) => (
                        <MenuItem
                          key={id}
                          label={`${id}.${title}`}
                          value={`${id}`}
                        >
                          {title}
                        </MenuItem>
                      ))}
                    </Select>
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="users"
                control={control}
                render={({ field }) => {
                  return (
                    <Select
                      label="Usuarios"
                      fullWidth
                      multiple
                      enableFilter
                      maxItems={5}
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.users}
                      helperText={errors.users && errors.users.message}
                    >
                      {users &&
                        users.map(({ username, id }) => (
                          <MenuItem
                            key={username}
                            label={`${username}`}
                            value={`${id}`}
                          >
                            {username}
                          </MenuItem>
                        ))}
                    </Select>
                  );
                }}
              />
            </Grid>
            <Grid item>
              <Button
                fullWidth
                variant="text"
                color="grey"
                href="/pages/management/role"
              >
                Cancelar
              </Button>
            </Grid>
            <Grid item>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                loading={loading}
              >
                Crear
              </Button>
            </Grid>
          </Grid>
          <SnackBar
            wait={2000}
            open={open !== undefined}
            onClose={onClose}
            severity={open}
          >
            {open === 'success' && 'Se creo el rol con exito'}
            {open === 'error' && 'Ocurrió un error, intenta nuevamente'}
          </SnackBar>
        </form>
      ) : (
        <CreateRoleTemplateLoading />
      )}
    </Paper>
  );
};

export default CreateTaskTemplate;
